import { alpha, type SxProps, type Theme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAward } from "@fortawesome/pro-solid-svg-icons/faAward";
import { faBuilding } from "@fortawesome/pro-solid-svg-icons/faBuilding";
import { faCircleCheck } from "@fortawesome/pro-solid-svg-icons/faCircleCheck";
import { faLaptop } from "@fortawesome/pro-solid-svg-icons/faLaptop";
import { faLaptopHouse } from "@fortawesome/pro-solid-svg-icons/faLaptopHouse";
import { ProgramEnvironmentToNameMap } from "@portal/app/_common/lib/programHelpers";
import type { ProgramEnvironment } from "@portal/app/_common/types/program";
import type {
  HostedFlowConfig,
  PayloadFormQuestion,
  PayloadProgramHighlight,
} from "@portal/app/_server/services/formFunnel/payloadTypes";
import { FunnelButton } from "../FunnelButton";
import type { ProgramClickoutData } from "./ClickoutResult";
import type { HostedResultProps } from "./HostedResult";

const MAX_VISIBLE_HIGHLIGHTS = 3;

// Visual spec comes from the ENG-7384 "Hybrid Funnel" prototype attached to the
// ticket. Its design tokens are the same set the MUI theme already carries
// (--blue-600 = primary.main, --cream-100 = dream.grayLight, --border =
// dream.borderColor, the aid chip = success.light/dark), so those are read from
// the theme rather than repeated here. The pale blue the best-match card fades
// from is the only value with no theme token yet; it is the same tint the
// marketing homepage gradient uses.
const BEST_MATCH_GRADIENT = "linear-gradient(180deg, #e8f2ff, #ffffff 120px)";
const CARD_RADIUS = "20px"; // --radius-lg

/**
 * Chips are pill-shaped and larger than MUI's default, so the label/icon padding
 * is zeroed and re-applied on the root to hit the design's 5px/11px box.
 */
const RESULT_CHIP_SX = {
  height: "auto",
  borderRadius: "40px", // --radius-pill
  fontSize: "0.82rem",
  fontWeight: 600,
  py: "5px",
  px: "11px",
  "& .MuiChip-label": { p: 0 },
  // `inherit` keeps each icon on its chip's text color; MUI otherwise applies
  // its own muted icon color and the green aid check would render gray. `p: 0`
  // also clears the theme-wide MuiChip override (components.tsx sets an icon
  // paddingLeft), which would otherwise push the icon off the chip's leading edge.
  "& .MuiChip-icon": { m: 0, p: 0, mr: 0.75, fontSize: "0.85rem", color: "inherit" },
};

// `program_environment` is an optional funnel filter, so a ranked list can mix
// delivery modes — the icon has to follow the label rather than assume online.
const ENVIRONMENT_ICONS: Record<ProgramEnvironment, IconDefinition> = {
  ONLINE: faLaptop,
  HYBRID: faLaptopHouse,
  IN_PERSON: faBuilding,
};

/**
 * Program copy remains available to the hosted flow's converting button, but it
 * is intentionally not surfaced on clickout or hosted chooser cards. Clickout
 * cards may instead use funnel-level copy.
 */
const RESULT_CTA = "Request info";

// Composed once at module load — neither chip varies with props or state.
// `dream.*` is read through a theme callback rather than a "dream.grayLight"
// string path: the string form resolves to undefined (and silently drops the
// color) if the namespace is ever missing, where the callback throws.
const ENVIRONMENT_CHIP_SX: SxProps<Theme> = {
  ...RESULT_CHIP_SX,
  bgcolor: (theme: Theme) => theme.palette.dream.grayLight,
  color: (theme: Theme) => theme.palette.dream.grayMediumDark,
  border: "1px solid",
  borderColor: (theme: Theme) => theme.palette.dream.borderColor,
};

const FINANCIAL_AID_CHIP_SX: SxProps<Theme> = {
  ...RESULT_CHIP_SX,
  bgcolor: "success.light",
  color: "success.dark",
};

export type ProgramResultOption =
  | {
      type: "CLICKOUT";
      rank: number;
      program: ProgramClickoutData;
      staticProgramHighlights?: PayloadProgramHighlight[];
      prePingResult?: unknown;
    }
  | {
      type: "HOSTED";
      rank: number;
      program: HostedResultProps["program"];
      hostedFlowConfig?: HostedFlowConfig;
      funnelQuestions?: PayloadFormQuestion[];
      staticProgramHighlights?: PayloadProgramHighlight[];
      prePingResult?: unknown;
      searchUrl?: string;
    };

interface ProgramResultCardProps {
  option: ProgramResultOption;
  clickoutCtaText?: string;
  isSubmitting?: boolean;
  isPreview?: boolean;
  onSelect: () => void;
  programHeadingComponent?: "h1" | "h2" | "h3";
  /** Retains the single-clickout selector while the shared card keeps ranked-card selectors. */
  ctaTestId?: string;
}

const getProviderName = (option: ProgramResultOption): string =>
  option.type === "CLICKOUT" ? option.program.provider.name : option.program.provider.fullName;

const getHighlights = (option: ProgramResultOption): string[] => {
  if (option.staticProgramHighlights?.length) {
    return option.staticProgramHighlights
      .map((highlight) =>
        highlight.heading ? `${highlight.heading}: ${highlight.text}` : highlight.text
      )
      .filter(Boolean)
      .slice(0, MAX_VISIBLE_HIGHLIGHTS);
  }
  return (option.program.programHighlights ?? []).slice(0, MAX_VISIBLE_HIGHLIGHTS);
};

export const ProgramResultCard = ({
  option,
  clickoutCtaText,
  isSubmitting = false,
  isPreview = false,
  onSelect,
  programHeadingComponent = "h2",
  ctaTestId,
}: ProgramResultCardProps) => {
  const providerName = getProviderName(option);
  const providerLogoUrl = option.program.provider.logoImageFile?.url;
  const environment = option.program.environment;
  const highlights = getHighlights(option);
  const isBestMatch = option.rank === 1;
  // clickoutConfig is unvalidated JSON, so a stored ctaText may not be a string.
  const trimmedClickoutCta = typeof clickoutCtaText === "string" ? clickoutCtaText.trim() : "";
  const ctaText =
    option.type === "CLICKOUT" && trimmedClickoutCta ? trimmedClickoutCta : RESULT_CTA;

  return (
    <Box
      data-testid={`multi-result-card-${option.rank}`}
      data-program-id={option.program.id}
      data-provider-id={option.program.provider.id}
      sx={{
        overflow: "hidden",
        borderRadius: CARD_RADIUS,
        bgcolor: "background.paper",
        border: isBestMatch ? "2px solid" : "1px solid",
        borderColor: (theme) =>
          isBestMatch ? theme.palette.primary.main : theme.palette.dream.borderColor,
        boxShadow: (theme) =>
          isBestMatch ? `0 8px 24px ${alpha(theme.palette.primary.main, 0.14)}` : "none",
        ...(!isBestMatch && {
          "&:hover": { boxShadow: "0 12px 32px rgba(0, 0, 0, 0.12)" },
        }),
      }}
    >
      {isBestMatch && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            bgcolor: "primary.main",
            color: "primary.contrastText",
            px: 2,
            py: "9px",
            fontSize: "0.74rem",
            fontWeight: 800,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
          }}
        >
          <FontAwesomeIcon icon={faAward} />
          Best match for you
        </Box>
      )}

      <Box
        sx={{
          p: isBestMatch ? "18px 18px 20px" : "16px 16px 18px",
          ...(isBestMatch && { background: BEST_MATCH_GRADIENT }),
        }}
      >
        {/* Let the name wrap below the logo in narrow cards, including desktop
            split layouts, so wide school wordmarks have room to stay legible. */}
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.25, mb: 1.25 }}>
          {providerLogoUrl ? (
            <Box
              data-testid={`multi-result-provider-logo-${option.rank}`}
              sx={{
                width: "12.5rem",
                maxWidth: "100%",
                height: "5rem",
                boxSizing: "border-box",
                borderRadius: "10px",
                bgcolor: "background.paper",
                border: "1px solid",
                borderColor: (theme) => theme.palette.dream.borderColor,
                p: "4px",
                flexShrink: 0,
              }}
            >
              <img
                src={providerLogoUrl}
                alt={`${providerName} logo`}
                style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
              />
            </Box>
          ) : (
            <Box
              aria-hidden
              data-testid={`multi-result-rank-${option.rank}`}
              sx={{
                width: 30,
                height: 30,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                fontWeight: 800,
                fontSize: "0.95rem",
                bgcolor: (theme) =>
                  isBestMatch ? theme.palette.primary.main : theme.palette.dream.grayLight,
                color: (theme) =>
                  isBestMatch
                    ? theme.palette.primary.contrastText
                    : theme.palette.dream.grayMediumDark,
              }}
            >
              {option.rank}
            </Box>
          )}
          <Typography
            component="span"
            sx={{
              flex: "1 1 11.5rem",
              minWidth: 0,
              overflowWrap: "anywhere",
              fontWeight: 700,
              fontSize: isBestMatch ? "1rem" : "0.98rem",
              lineHeight: 1.2,
              color: "text.primary",
            }}
          >
            {providerName}
          </Typography>
        </Box>

        <Typography
          component={programHeadingComponent}
          sx={{
            m: 0,
            mb: 1,
            overflowWrap: "anywhere",
            fontSize: isBestMatch ? "1.25rem" : "1.18rem",
            fontWeight: 700,
            lineHeight: 1.25,
            color: "text.primary",
          }}
        >
          {option.program.name}
        </Typography>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
          {environment && (
            <Chip
              icon={<FontAwesomeIcon icon={ENVIRONMENT_ICONS[environment]} />}
              label={ProgramEnvironmentToNameMap[environment]}
              sx={ENVIRONMENT_CHIP_SX}
            />
          )}
          {(option.program.hasFinancialAidOption ?? false) && (
            <Chip
              icon={<FontAwesomeIcon icon={faCircleCheck} />}
              label="Financial aid may be available"
              sx={FINANCIAL_AID_CHIP_SX}
            />
          )}
        </Box>

        {highlights.length > 0 && (
          <Box
            component="ul"
            sx={{
              m: 0,
              mb: isBestMatch ? 1.75 : 1.5,
              pl: "18px",
              display: "flex",
              flexDirection: "column",
              gap: "5px",
              overflowWrap: "anywhere",
              color: (theme) => theme.palette.dream.grayMediumDark,
            }}
          >
            {highlights.map((highlight) => (
              <Typography
                key={highlight}
                component="li"
                sx={{ fontSize: isBestMatch ? "0.95rem" : "0.92rem", lineHeight: 1.5 }}
              >
                {highlight}
              </Typography>
            ))}
          </Box>
        )}

        <FunnelButton
          onClick={onSelect}
          disabled={isSubmitting || (option.type === "CLICKOUT" && isPreview)}
          appearance={isBestMatch ? "filled" : "outlined"}
          icon={option.type === "CLICKOUT" ? "external" : "none"}
          sx={{
            maxWidth: "none",
            fontSize: isBestMatch ? "1.05rem" : "1rem",
            fontWeight: 600,
            // Doubled so the design's taller CTA actually applies: the theme
            // sizes every non-text button with `&:not(.MuiButton-text)`
            // (components.tsx), which outweighs a single-class sx rule and
            // would otherwise pin these back to 3rem.
            "&&": { height: isBestMatch ? 52 : 50 },
          }}
          data-testid={ctaTestId ?? `multi-result-cta-${option.rank}`}
          data-navigation-action="select_program"
        >
          {ctaText}
        </FunnelButton>
      </Box>
    </Box>
  );
};
